/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import '@a11y/focus-trap';
import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { KEYCODE } from '../../utils/keycode.js';

/**
 * @prop {String} label - Defines if the modal is shown (true) or not (false).
 * @prop {String} modalHeight - Defines the height of the modal. Takes a number or css attribute (e.g. fit-content).
 * @attr {String} modal-height -Defines the height of the modal. Takes a number or css attribute (e.g. fit-content).
 * @prop {String} modalWidth - Defines the width of the modal. Takes a number or css attribute (e.g. fit-content).
 * @attr {String} modal-width - Defines the width of the modal. Takes a number or css attribute (e.g. fit-content).
 * @prop {String} type - Defines the type of modal that will be displayed. Accepted values are error, alert and confirmation.
 * @prop {String} icon - Optional property that reflects the icon of the modal.
 * @prop {Boolean} sticky - Defines that the modal can only be closed through actions present in the modal. The close icon will also be hidden.
 * @prop {Boolean} visible - Defines if the modal is shown or not.
 * @slot - The slot where the main content is rendered.
 * @slot header - Takes children such as tabs.
 * @slot functions - Takes children such as icons.
 * @slot footer - Takes buttons as children.
 * @fires modal-closed - Dispatched when closing the modal.
 */

export class SHModal extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      icon: { type: String, reflect: true },
      type: { type: String, reflect: true },
      modalHeight: { type: String, reflect: true, attribute: 'modal-height' },
      modalWidth: { type: String, reflect: true, attribute: 'modal-width' },
      visible: { type: Boolean, reflect: true },
      sticky: { type: Boolean, reflect: true },
      _emptyFooter: { type: Boolean },
      _emptyHeader: { type: Boolean },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          visibility: hidden;
          position: fixed;
          top: 0rem;
          left: 0rem;
          width: 100%;
          height: 0rem;
          max-height: 0rem;
          overflow: hidden;
          opacity: 0;
          z-index: 5;
          align-items: center;
          justify-content: center;
          display: flex;
          outline: transparent;
          background: rgba(0, 0, 0, 0.4); /* Modal Background (UI 1 - Light / UI 7 - Dark) */
          transition: var(--transition-time) all ease-in-out,
            0s height ease-in-out var(--transition-time),
            0s max-height ease-in-out var(--transition-time), 0s visibility linear 0s;
        }

        :host([visible]) {
          visibility: visible;
          opacity: 1;
          height: 100%;
          max-height: 100%;
          transition: var(--transition-time) all ease-in-out var(--transition-time),
            0s height ease-in-out, 0s max-height ease-in-out, 0s visibility linear 0s;
        }

        :host([visible]) .modal-wrapper {
          transform: none;
          transition: var(--transition-time) all ease-in-out var(--transition-time);
        }

        .modal-wrapper {
          transition: var(--transition-time) all ease-in-out;
          transform: translateY(2.5rem);
          background: var(--base-3);
          box-shadow: var(--shadow-navigation);
          display: flex;
          flex-direction: column;
          border-radius: 0.125rem;
          z-index: 5;
          position: relative;
        }

        .focus-wrapper {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: auto;
        }

        .label-wrapper {
          display: flex;
          flex-direction: row;
          padding: 1rem;
          align-items: center;
          min-height: fit-content;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
        }

        .modal-label {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: var(--text-primary);
          font: var(--header-2);
          line-height: 1.5rem;
          flex: 1;
          -webkit-font-smoothing: antialiased;
          text-rendering: optimizeLegibility;
          -moz-osx-font-smoothing: grayscale;
        }

        .close-button-2 {
          margin: 1rem 1rem 0rem 0.5rem;
        }

        .functions-wrapper {
          display: flex;
          flex-direction: row;
          align-items: center;
        }

        .functions-wrapper > ::slotted(*),
        .footer-wrapper > ::slotted(*) {
          margin-left: 0.5rem;
        }

        .header-wrapper {
          display: flex;
          flex-direction: row;
        }

        .header-slot-wrapper > ::slotted(:not(sh-tabs)) {
          margin: 1rem;
        }

        .header-slot-wrapper > ::slotted(sh-tabs) {
          margin-bottom: 1rem;
        }

        .header-slot-wrapper {
          flex: 1 1 0%;
          display: flex;
          overflow: hidden;
        }

        .body-wrapper {
          flex: 1;
          padding: 1rem;
          box-sizing: border-box;
          display: block;
          flex-direction: column;
          overflow: auto;
          font: var(--body-1);
          color: var(--text-primary);
          line-height: 1.25rem;
        }

        .body-wrapper > ::slotted(*) {
          margin-bottom: 0.5rem;
        }

        .footer-wrapper {
          min-height: fit-content;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
          padding: 1rem;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          justify-content: flex-end;
          align-items: center;
        }

        .footer-wrapper > ::slotted(*) {
          color: var(--text-primary);
          font: var(--body-2);
          line-height: 1rem;
        }

        .close-button {
          transition: var(--transition-time) all ease-in-out;
          margin-left: 0.5rem;
        }

        .modal-wrapper[empty-header] .header-wrapper {
          display: none;
        }

        :host(:not([label])) .label-wrapper {
          display: none;
        }

        .modal-wrapper[empty-footer] .footer-wrapper {
          display: none;
        }

        .modal-wrapper:not([empty-header]) .label-wrapper {
          display: none;
        }

        .modal-wrapper:not([empty-header]) .body-wrapper,
        :host([label]) .body-wrapper {
          padding-top: 0rem;
        }

        .modal-wrapper:not([empty-footer]) .body-wrapper {
          padding-bottom: 0rem;
        }

        /* Modal Type Styles */
        .modal-wrapper .label-wrapper sh-icon:not(.close-button) {
          margin-right: 0.5rem;
          margin-left: 0rem;
        }

        /* touch */
        @media (any-pointer: coarse) {
          .functions-wrapper > ::slotted(*),
          .footer-wrapper > ::slotted(*) {
            margin-left: 1rem;
          }
        }

        .body-wrapper > ::slotted(sh-badge) {
          display: block;
        }
      `,
    ];
  }

  render() {
    return html`
      <div
        class="modal-wrapper"
        style="height: ${this.modalHeight}; width: ${this.modalWidth}"
        ?empty-header=${this._emptyHeader}
        ?empty-footer=${this._emptyFooter}
      >
        <focus-trap class="focus-wrapper">
          <div class="label-wrapper">
            ${this.icon
              ? html` <sh-icon icon="${this.icon}" class="modal-icon" size="s"></sh-icon>`
              : ''}
            ${this.type === 'error' ? html` <sh-icon icon="error" size="s"></sh-icon>` : ''}
            ${this.type === 'alert' || this.type === 'warning'
              ? html` <sh-icon icon="warning" size="s"></sh-icon>`
              : ''}
            ${this.type === 'confirmation' || this.type === 'success'
              ? html` <sh-icon icon="success" size="s"></sh-icon>`
              : ''}
            <div class="modal-label">${this.label}</div>
            <div class="functions-wrapper">
              <slot name="functions"></slot>
              ${!this.sticky
                ? html` <sh-icon
                    button
                    icon="cancel"
                    class="close-button"
                    size="s"
                    @click="${() => this._closeModal()}"
                  ></sh-icon>`
                : ''}
            </div>
          </div>
          <div class="header-wrapper">
            <div class="header-slot-wrapper">
              <slot
                name="header"
                class="header"
                @slotchange="${(e) => this._updateSlotFlag(e.target, '_emptyHeader')}"
              ></slot>
            </div>
            ${!this.sticky
              ? html` <sh-icon
                  button
                  icon="cancel"
                  class="close-button-2"
                  size="s"
                  @click="${() => this._closeModal()}"
                ></sh-icon>`
              : ''}
          </div>
          <div class="body-wrapper">
            <slot></slot>
          </div>
          <div class="footer-wrapper">
            <slot
              name="footer"
              class="footer"
              @slotchange="${(e) => this._updateSlotFlag(e.target, '_emptyFooter')}"
            ></slot>
          </div>
        </focus-trap>
      </div>
    `;
  }

  constructor() {
    super();
    this.modalHeight = '30rem';
    this.modalWidth = '30rem';
    this.visible = false;
    this._emptyFooter = true;
    this._emptyHeader = true;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'dialog');
    }
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('visible')) {
      this._handleVisibleChanged();
    }
    const listOfProperties = [
      'label',
      'visible',
      'type',
      'icon',
      'sticky',
      'modalHeight',
      'modalWidth',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  firstUpdated() {
    this.addEventListener('mousedown', this._closeNonstickyModal);
    this.addEventListener('dblclick', this._closeNonstickyModal);
    const modalWrapper = this.shadowRoot.querySelector('.modal-wrapper');
    modalWrapper.addEventListener('mousedown', function (e) {
      e.stopPropagation();
    });
    modalWrapper.addEventListener('dblclick', function (e) {
      e.stopPropagation();
    });
    this._keyEventHandler = this._handleKeyup.bind(this);
  }

  _updateSlotFlag(slot, emptySlotFlag) {
    const nodes = slot.assignedNodes({
      flatten: true,
    }).length;
    if (nodes === 0) {
      this[emptySlotFlag] = true;
    } else {
      this[emptySlotFlag] = false;
    }
  }

  _handleVisibleChanged() {
    if (this.visible) {
      this.focus();
      this.shadowRoot.querySelector('.modal-wrapper').style.outline = '0';
      document.addEventListener('keyup', this._keyEventHandler);
    } else {
      document.removeEventListener('keyup', this._keyEventHandler);
    }
  }

  _handleKeyup(e) {
    e.stopPropagation();
    if (e.keyCode === KEYCODE.ESCAPE) {
      this._closeNonstickyModal();
    }
    if (e.keyCode === KEYCODE.TAB) {
      if (!this.contains(e.target)) {
        this.focus();
      }
    }
  }

  _closeNonstickyModal() {
    // Checking for sticky modal
    if (!this.sticky) {
      this._closeModal();
    }
  }

  _closeModal() {
    this.visible = false;
    // fire event on closing
    dispatchCustomEvent(this, 'modal-closed');
  }

  disconnectedCallback() {
    document.removeEventListener('keyup', this._keyEventHandler);
    super.disconnectedCallback();
  }
}

customElements.define('sh-modal', SHModal);
